/**
 * API client for the Longship Review UI.
 *
 * Wraps native fetch with:
 *   - Authorization: Bearer <keycloak token> (when SSO session is active)
 *   - x-api-key header (from VITE_API_KEY)
 *   - JSON content-type
 *   - 10s timeout via AbortController
 *   - Normalized error shape: { code, message, issues, status }
 *   - Unwraps { data } envelope from the Express API
 *
 * All functions return the unwrapped `data` payload (not the raw response),
 * and throw an ApiError on non-2xx responses.
 */

import { getSsoAccessToken } from "./auth.js";

const BASE_URL = import.meta.env.VITE_API_URL || "/api/v1";
const API_KEY = import.meta.env.VITE_API_KEY || "";
const TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Error class
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor({ code, message, issues, status }) {
    super(message || code || `API error (${status})`);
    this.name = "ApiError";
    this.code = code || "API_ERROR";
    this.status = status ?? 0;
    this.issues = issues ?? [];
  }
}

// ---------------------------------------------------------------------------
// Core request helper
// ---------------------------------------------------------------------------

async function request(path, { method = "GET", body, query, signal: extSignal, timeoutMs } = {}) {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }

  const controller = new AbortController();
  const timeout = timeoutMs ?? TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeout);
  // If the caller passed an external signal, abort when either fires.
  if (extSignal) {
    if (extSignal.aborted) controller.abort();
    else extSignal.addEventListener("abort", () => controller.abort());
  }

  const headers = { "content-type": "application/json", accept: "application/json" };
  if (API_KEY) headers["x-api-key"] = API_KEY;
  // Keycloak bearer. Awaited so a token refresh (updateToken) completes before
  // the request goes out; null when SSO is off or not signed in.
  const ssoToken = await getSsoAccessToken();
  if (ssoToken) headers["authorization"] = `Bearer ${ssoToken}`;

  let res;
  try {
    res = await fetch(url.toString().replace(window.location.origin, ""), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === "AbortError") {
      throw new ApiError({
        code: "TIMEOUT",
        message: `Request timed out after ${timeout}ms`,
        status: 0,
      });
    }
    throw new ApiError({
      code: "NETWORK_ERROR",
      message: err.message || "Network request failed",
      status: 0,
    });
  } finally {
    clearTimeout(timer);
  }

  // Parse JSON body (the API always returns JSON, even on error).
  let payload = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }

  if (!res.ok) {
    const errBody = payload?.error || payload || {};
    throw new ApiError({
      code: errBody.code || `HTTP_${res.status}`,
      message: errBody.message || res.statusText || `HTTP ${res.status}`,
      issues: errBody.issues || errBody.details || [],
      status: res.status,
    });
  }

  // API envelopes list+single responses as { data, ...meta }.
  return payload?.data !== undefined ? payload.data : payload;
}

// ---------------------------------------------------------------------------
// IO endpoints — /api/v1/ios
// ---------------------------------------------------------------------------

/**
 * List IOs.
 * @param {object} [opts]
 * @param {string} [opts.status]     Filter by status (draft, pending, approved, rejected).
 * @param {string} [opts.advertiser] Substring match on advertiser_name.
 * @param {string} [opts.agency]    Substring match on agency.
 * @param {number} [opts.page=1]
 * @param {number} [opts.limit=50]
 * @returns {Promise<{ data: object[], total: number, page: number, limit: number }>}
 */
export function getIos(opts = {}) {
  return request("/ios", { query: opts });
}

/** Get a single IO by id (io_number). */
export function getIo(id) {
  return request(`/ios/${encodeURIComponent(id)}`);
}

/** Update an IO (partial PUT). */
export function updateIo(id, patch) {
  return request(`/ios/${encodeURIComponent(id)}`, { method: "PUT", body: patch });
}

/** Approve an IO. Body may include { approver }. */
export function approveIo(id, body = {}) {
  return request(`/ios/${encodeURIComponent(id)}/approve`, { method: "POST", body });
}

/** Reject an IO. Body must include { reason }. */
export function rejectIo(id, body) {
  return request(`/ios/${encodeURIComponent(id)}/reject`, { method: "POST", body });
}

/** Soft-delete an IO (status -> "deleted"; excluded from default listings). */
export function deleteIo(id) {
  return request(`/ios/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Set an IO's status directly (draft/pending/approved/rejected/deleted). */
export function setIoStatus(id, status) {
  return request(`/ios/${encodeURIComponent(id)}/status`, { method: "PATCH", body: { status } });
}

/** Trigger LLM re-extraction on the original document. Returns a job handle. */
export function regenerateIo(id, body = {}) {
  return request(`/ios/${encodeURIComponent(id)}/regenerate`, { method: "POST", body });
}

/**
 * Record a conflict resolution action on an IO (PRD §9.3).
 * @param {string} id - IO id
 * @param {object} body - { code, path?, action, value?, message?, actor? }
 *   action: "accept" | "override" | "escalate" | "request_clarification"
 * @returns {Promise<{ ioId, resolution, io }>}
 */
export function resolveConflict(id, body) {
  return request(`/ios/${encodeURIComponent(id)}/conflicts/resolve`, {
    method: "POST",
    body,
  });
}

/** Get all conflict resolutions for an IO. */
export function getConflictResolutions(id) {
  return request(`/ios/${encodeURIComponent(id)}/conflicts/resolutions`);
}

// ---------------------------------------------------------------------------
// Campaign endpoints — /api/v1/campaigns
// ---------------------------------------------------------------------------

/** List campaigns. Filter by { status, ioId, page, limit }. */
export function getCampaigns(opts = {}) {
  return request("/campaigns", { query: opts });
}

/** Get a single campaign. */
export function getCampaign(id) {
  return request(`/campaigns/${encodeURIComponent(id)}`);
}

/** Get generation status for a campaign job. */
export function getCampaignStatus(id) {
  return request(`/campaigns/${encodeURIComponent(id)}/status`);
}

/**
 * Trigger campaign generation for an approved IO.
 * @param {string} ioId  The IO id (io_number).
 * @returns {Promise<object>} job record { id, ioId, status, ... }
 */
export function generateCampaign(ioId, userId) {
  return request(`/campaigns/${encodeURIComponent(ioId)}/generate`, { method: "POST", body: userId ? { userId } : {} });
}

/** Run a dry-run preview for a campaign job (no commit). */
export function dryRunCampaign(campaignId) {
  return request(`/campaigns/${encodeURIComponent(campaignId)}/dry-run`, {
    method: "POST",
    body: {},
  });
}

/** Run a synchronous dry-run preview for an IO (no campaign job needed). */
export function dryRunPreview(ioId) {
  return request(`/campaigns/dry-run`, {
    method: "POST",
    body: { ioId },
  });
}

// ---------------------------------------------------------------------------
// Reference data (admin/lookup endpoints — may 404 in MVP; callers should
// tolerate undefined).
// ---------------------------------------------------------------------------

/** List configured platforms. Tolerates 404 (returns []). */
export async function getPlatforms() {
  try {
    return await request("/admin/platforms");
  } catch (err) {
    if (err.status === 404) return [];
    throw err;
  }
}

/** List agency prompt templates. Tolerates 404 (returns []). */
export async function getAgencyTemplates() {
  try {
    return await request("/admin/templates");
  } catch (err) {
    if (err.status === 404) return [];
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Dedup endpoints — /api/v1/admin/dedup
// ---------------------------------------------------------------------------

/**
 * Query the deduplication audit log.
 * @param {object} [opts]
 * @param {string} [opts.status] - "accepted" | "duplicate_file" | "duplicate_io" | "overridden"
 * @param {string} [opts.source] - "upload" | "email" | "slack" | "api"
 * @param {string} [opts.ioNumber]
 * @param {number} [opts.page=1]
 * @param {number} [opts.limit=50]
 * @returns {Promise<{ data: object[], total: number, page: number, limit: number }>}
 */
export function getDedupLog(opts = {}) {
  return request("/admin/dedup", { query: opts });
}

/** Get dedup summary stats. */
export function getDedupStats() {
  return request("/admin/dedup/stats");
}

/**
 * Override a duplicate flag (manually accept a flagged duplicate).
 * @param {string} entryId
 * @param {string} reason
 */
export function overrideDedupEntry(entryId, reason) {
  return request(`/admin/dedup/${encodeURIComponent(entryId)}/override`, {
    method: "POST",
    body: { reason },
  });
}

// ---------------------------------------------------------------------------
// Audit log — /api/v1/admin/audit-log
// ---------------------------------------------------------------------------

/**
 * Query the immutable audit log.
 * @param {object} [opts]
 * @param {string} [opts.ioId]
 * @param {string} [opts.actor]
 * @param {string} [opts.from] - ISO date
 * @param {string} [opts.to] - ISO date
 * @param {number} [opts.page=1]
 * @param {number} [opts.limit=100]
 */
export function getAuditLog(opts = {}) {
  return request("/admin/audit-log", { query: opts });
}

// ---------------------------------------------------------------------------
// Compliance — /api/v1/compliance
// ---------------------------------------------------------------------------

/**
 * Get a Lawspeaker compliance verdict for a campaign, via Longship's own API
 * (which signs the Lawspeaker request server-side). The browser never talks
 * to Lawspeaker directly — that would require shipping a Lawspeaker
 * credential in the JS bundle.
 * @param {object} body - { campaign, targeting, context } — see @lawspeaker/schemas VerdictRequest.
 * @returns {Promise<object>} verdict response, or { enabled: false } if the gate is disabled server-side.
 */
export function getComplianceVerdict(body) {
  return request("/compliance/verdict", { method: "POST", body });
}

/**
 * Run Lawspeaker compliance checks server-side against all IOs (or a subset)
 * and persist one verdict record per io_number.
 * @param {string[]} [ioNumbers] - restrict to specific IO numbers.
 * @returns {Promise<object>} { data: { checked, skipped, results, summary } }
 */
export function runComplianceChecks(ioNumbers = null) {
  return request("/compliance/checks/run", {
    method: "POST",
    body: ioNumbers ? { io_numbers: ioNumbers } : {},
    timeoutMs: 120000,
  });
}

/**
 * List persisted compliance checks.
 * @param {{ verdict?: string, ioNumber?: string, page?: number, limit?: number }} opts
 */
export function getComplianceChecks(opts = {}) {
  return request("/compliance/checks", { query: opts });
}

/** Verdict breakdown for the Policies page KPI cards. */
export function getComplianceChecksSummary() {
  return request("/compliance/checks/summary");
}

/**
 * Lawspeaker corpus freshness SLO report (30d political / 90d standard).
 * Returns { enabled, freshness } where freshness is null when Lawspeaker is
 * unreachable or predates the freshness feature.
 */
export function getPolicyFreshness() {
  return request("/compliance/checks/policy-freshness");
}

/**
 * Lawspeaker pending_review queue: policy rows the reverify crawler flagged
 * as diverging from their cited source. Returns { enabled, reviews, total }.
 */
export function getPolicyReviewQueue() {
  return request("/compliance/checks/policy-review");
}

/**
 * Resolve one pending_review proposal. action: "apply" merges the proposed
 * status/notes into the corpus; "reject" drops it. Returns the Lawspeaker
 * resolve result { applied, rejected, missing }.
 */
export function resolvePolicyReview(id, action) {
  return request(`/compliance/checks/policy-review/${encodeURIComponent(id)}/resolve`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

/** Latest persisted compliance check for one IO. */
export function getComplianceCheckForIo(ioNumber) {
  return request(`/compliance/checks/${encodeURIComponent(ioNumber)}`);
}

// ---------------------------------------------------------------------------
// HubSpot — /api/v1/hubspot
// ---------------------------------------------------------------------------

/** HubSpot adapter health check. */
export function getHubSpotHealth() {
  return request("/hubspot/health");
}

/**
 * Sync a single HubSpot deal by ID into Longship as a draft IO.
 * @param {string} dealId
 */
export function syncHubSpotDeal(dealId) {
  return request(`/hubspot/sync/${encodeURIComponent(dealId)}`, { method: "POST", body: {} });
}

/** Bulk sync all closed-won HubSpot deals. */
export function syncAllHubSpotDeals() {
  return request("/hubspot/sync-all", { method: "POST", body: {} });
}

// ---------------------------------------------------------------------------
// Agency templates — /api/v1/admin/agency-templates
// ---------------------------------------------------------------------------

/** List agency prompt templates. Tolerates 404 (returns []). */
export async function getAgencyTemplatesList() {
  try {
    return await request("/admin/agency-templates");
  } catch (err) {
    if (err.status === 404) return { data: [] };
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Platform connection test
// ---------------------------------------------------------------------------

/** Test connection to a configured ad platform. */
export function testPlatformConnection(platform, userId) {
  return request(`/admin/platforms/${encodeURIComponent(platform)}/test`, { method: "POST", body: userId ? { userId } : {} });
}

// ---------------------------------------------------------------------------
// Platform Settings — per-user API key management
// ---------------------------------------------------------------------------

/** Get the current user's Adgentek platform settings. */
export function getPlatformSettings(userId) {
  return request("/admin/platform-settings", { query: { userId } });
}

/** Save the current user's Adgentek platform API key + config. */
export function savePlatformSettings(body) {
  return request("/admin/platform-settings", { method: "PUT", body });
}

/** Delete the current user's Adgentek platform API key. */
export function deletePlatformSettings(userId) {
  return request("/admin/platform-settings", { method: "DELETE", query: { userId } });
}

export default {
  ApiError,
  getIos,
  getIo,
  updateIo,
  approveIo,
  rejectIo,
  regenerateIo,
  resolveConflict,
  getConflictResolutions,
  getCampaigns,
  getCampaign,
  getCampaignStatus,
  generateCampaign,
  dryRunCampaign,
  dryRunPreview,
  getComplianceVerdict,
  getPlatforms,
  getAgencyTemplates,
  getDedupLog,
  getDedupStats,
  overrideDedupEntry,
  getAuditLog,
  getHubSpotHealth,
  syncHubSpotDeal,
  syncAllHubSpotDeals,
  getAgencyTemplatesList,
  testPlatformConnection,
};